import * as turf from '@turf/turf';

// global zoom index for all the pins
let currentZoomIndex = 0;
const zoomLevels = [10, 12, 15]

// To zoom to a general area of a location
function handlePinClick(map, coordinates, currentZoom, onMaxZoomReached) {
  // FInd the closest zoom level to the current zoom
  // that is greater than the current zoom
  const closestZoomIndex = zoomLevels.findIndex((zoom) => zoom > currentZoom);
  currentZoomIndex = closestZoomIndex !== - 1 ? closestZoomIndex : zoomLevels.length - 1;

  // determine target zoom level based on currentZoomIndex
  // prevents map from zooming out if user is zoomed in further than
  // the predefined zoom level
  const targetZoom = Math.max(currentZoom, zoomLevels[currentZoomIndex])

  // Adjust zoom duration based on zoom difference
  const baseDuration = 1000;
  const duration = baseDuration + (Math.abs(targetZoom - currentZoom) * 100);

  // zoom to the clicked pin's general location
  map.easeTo({
    center: coordinates,
    zoom: targetZoom,
    duration: duration,
    essential: true
  })

  // when the maximum zoom level is reached
  if (currentZoomIndex === zoomLevels.length - 1 && onMaxZoomReached) {
    map.once('moveend', onMaxZoomReached)
  }
}

document.addEventListener('DOMContentLoaded', function (event) {
  const DEFAULT_COORDS = [8.528400126589789, 47.38361809309874];
  const DEFAULT_ZOOM = 7;
  const LAYER_ID = 'locations-dataset';
  let userLocation = null;  // To store the user's location once it is fetched

  const currentLocale = document.getElementById('map').getAttribute('data-current-locale');

  // get access token and data
  function getMapboxAccessToken() {
    return document.getElementById('map').getAttribute("data-mapbox-access-token");
  }

  // get mapbox style url depending on env (prod, dev/test)
  function getMapboxStyleURL() {
    return document.getElementById('map').getAttribute("data-mapbox-style");
  }

  // get initial coordinates from HTML
  function getInitialCoordinates() {
    const initCoordsData = document
                            .getElementById('initCoords')
                            .getAttribute('data-init-coords');
    if (initCoordsData) {
      return initCoordsData.split(',').map(x => parseFloat(x));
    } else {
      return DEFAULT_COORDS;
    }
  }

  // get zoom level
  function getZoomLevel() {
    const zoomLevelData = document.getElementById('zoomLevel').getAttribute('data-zoom-level');
    if (zoomLevelData) {
      return zoomLevelData;
    } else {
      return DEFAULT_ZOOM;
    }
  }

  // detect user's language - depending on browser
  function getUserLanguage() {
    const userLanguage = navigator.language || navigator.userLanguage;
    return userLanguage.slice(0, 2);
  }

  // return language or default English
  function getPlaceholderText(language) {
    return MAP_SEARCH_LOCALES[language] || MAP_SEARCH_LOCALES["en"];
  }

  // initialize map
  function initializeMap() {
    mapboxgl.accessToken = getMapboxAccessToken();

    const map = new mapboxgl.Map({
      container: 'map',
      style: getMapboxStyleURL(),
      center: getInitialCoordinates(),
      zoom: getZoomLevel(),
      touchZoomRotate: true
    });

    map.on('load', () => {
      // ensure the layer LAYER_ID exists
      if (!map.getLayer(LAYER_ID)) {
        console.warn("Layer '${LAYER_ID}' not found in the style.");
        return;
      }

      // add click event listener to the layer for popups
      map.on('click', LAYER_ID, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [LAYER_ID]
        });

        if (features.length) {
          const feature = features[0];
          const coordinates = feature.geometry.coordinates.slice();
          const locationId = feature.properties['id'];
          const currentZoom = map.getZoom()

          let distanceText = "-" // default if user location not available
          if (userLocation) {
            const from = turf.point([userLocation.longitude, userLocation.latitude]);
            const to = turf.point(coordinates);
            const options = { units: 'kilometers' };

            const distance = turf.distance(from, to, options);
            distanceText = `${distance.toFixed(2)} km`;
          }

          handlePinClick(map, coordinates, currentZoom, () => {
            // create the popup content with a turbo frame
            const popupContent = `
              <turbo-frame
                id="location_popup"
                src="/maps/popup?id=${locationId}&locale=${currentLocale}"
                data-lng="${coordinates[0]}"
                data-lat="${coordinates[1]}"
                data-location-id="${locationId}">
              </turbo-frame>
            `;
            // create and display the pop-up
            new mapboxgl.Popup(
              {
                className: "custom-popup",
                closeOnClick: true
              }
            )
              .setLngLat(coordinates)
              .setHTML(popupContent)
              .addTo(map);
          });

          // Increment zoom state for this pin, or reset if max zoom is reached
          if (currentZoomIndex < zoomLevels.length - 1) {
            currentZoomIndex++
          }
        }
      });

      // change cursor style on hover to indicate interactivity
      map.on('mouseenter', LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      // revert cursor when not hovering
      map.on('mouseleave', LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
      });
    });
      
    return map;
  }

  // add map controls
  function addMapControls(map) {
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: true,
      fitBoundsOptions: {
        maxZoom: 13,
      },
    });

    map.addControl(geolocate, "top-left");

    // get user location
    geolocate.on('geolocate', (e) => {
      userLocation = e.coords;
    });

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      marker: true,
      placeholder: getPlaceholderText(getUserLanguage())
    });
    map.addControl(geocoder, "top-right");

    const nav = new mapboxgl.NavigationControl();
    map.addControl(nav, 'top-right');

    // trigger geolocation on load
    map.on('load', function () {
      geolocate.trigger();
    });
  }

  document.addEventListener('turbo:frame-load', function(event) {
    const frame = event.target;

    if (frame && frame.id === 'location_popup') {
      // Extract coordinates from data attributes
      const lng = parseFloat(frame.dataset.lng);
      const lat = parseFloat(frame.dataset.lat);

      let distanceText = "-"
      if (userLocation && !isNaN(lng) && !isNaN(lat)) {
        // turf.js distance calculation
        const from = turf.point([userLocation.longitude, userLocation.latitude]);
        const to = turf.point([lng, lat]);
        const options = { units: 'kilometers' };

        const distance = turf.distance(from, to, options);
        distanceText = `${distance.toFixed(2)} km`;
        }
        // Update the loaded Turbo Frame HTML with the distance info
        const distanceElement = frame.querySelector('.popup-distance');

        if (distanceElement) {
          distanceElement.innerHTML += `${distanceText}`;
      }
    }
  });

  // main initialization function
  function initialize() {
    const map = initializeMap();
    addMapControls(map);
  }

  initialize();
});
